import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Sparkles,
  User as UserIcon,
  X,
} from "lucide-react";
import { useNotifications } from "../../context/NotificationsContext";

/* -------------------------------------------------------------------------- */
/*  Visual maps                                                               */
/* -------------------------------------------------------------------------- */

const CATEGORY_VISUALS = {
  quota: {
    bg: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)",
    icon: AlertTriangle,
    iconColor: "#FFFFFF",
  },
  milestone: {
    bg: "linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)",
    icon: Sparkles,
    iconColor: "#FFFFFF",
  },
  account: {
    bg: "linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)",
    icon: UserIcon,
    iconColor: "#FFFFFF",
  },
  billing: {
    bg: "linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)",
    icon: CreditCard,
    iconColor: "#FFFFFF",
  },
};

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

const CategoryIcon = ({ category, dimmed }) => {
  const visual = CATEGORY_VISUALS[category] || CATEGORY_VISUALS.account;
  const Icon = visual.icon;
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-[13px]"
      style={{
        background: visual.bg,
        boxShadow: dimmed
          ? "0 1px 2px rgba(15,23,42,0.04)"
          : "0 2px 6px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.18)",
        opacity: dimmed ? 0.72 : 1,
      }}
      aria-hidden="true"
    >
      <Icon size={21} color={visual.iconColor} strokeWidth={2.1} />
    </span>
  );
};

const NotificationItem = ({ notification, onClick }) => {
  const { read, title, body, category } = notification;

  // Unread rows get a soft blue tint; read rows stay white.
  const baseBg = read ? "#FFFFFF" : "#E9F5FE";

  return (
    <div
      style={{ background: baseBg }}
      className="relative w-full text-left flex items-start gap-3.5 px-5 py-4 transition-colors duration-150"
    >
      <CategoryIcon category={category} dimmed={read} />

      <div className="min-w-0 flex-1">
        <h3
          className={`text-[15px] leading-snug tracking-[-0.005em] ${read
            ? "font-semibold text-[#64748B]"
            : "font-bold text-[#0F172A]"
            }`}
        >
          {title}
        </h3>

        {/* Body */}
        {body && (
          <p
            className={`mt-1 text-[13.5px] leading-[1.5] ${read ? "text-[#94A3B8]" : "text-[#64748B]"
              }`}
          >
            {body}
          </p>
        )}

        {/* Explicit action — only on unread */}
        {!read && (
          <button
            type="button"
            onClick={onClick}
            className="mt-2.5 text-[13.5px] font-semibold text-[#2F9BF0] hover:text-[#1E88E5] transition-colors focus:outline-none"
          >
            Mark as read
          </button>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ tab }) => (
  <div className="px-6 py-14 text-center">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB] mb-4 shadow-inner">
      <CheckCircle2 size={26} className="text-[#94A3B8]" strokeWidth={1.8} />
    </div>
    <div className="text-[14px] font-semibold text-[#0F172A] tracking-[-0.005em]">
      {tab === "unread" ? "You're all caught up" : "No notifications yet"}
    </div>
    <div className="mt-1.5 text-[12.5px] text-[#64748B] leading-snug max-w-[260px] mx-auto">
      {tab === "unread"
        ? "New notifications will appear here as they arrive."
        : "We'll let you know when something important happens."}
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/*  Main dropdown                                                             */
/* -------------------------------------------------------------------------- */

const NotificationDropdown = ({ onClose, anchorRef }) => {
  const panelRef = useRef(null);
  const [tab, setTab] = useState("all"); // 'all' | 'unread'
  const { notifications, unreadCount, markAsRead, markAllRead } =
    useNotifications();

  useEffect(() => {
    const onDown = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose, anchorRef]);

  const visible = useMemo(
    () =>
      tab === "unread"
        ? notifications.filter((n) => !n.read)
        : notifications,
    [notifications, tab]
  );

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-black/35 backdrop-blur-[2px] z-[120] sm:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notifications"
        className="
          fixed inset-x-0 bottom-0 top-[64px] z-[130] bg-white flex flex-col
          sm:absolute sm:inset-auto sm:right-0 sm:top-[calc(100%+12px)]
          sm:w-[420px] sm:max-w-[calc(100vw-24px)] sm:h-auto
          sm:min-h-[440px] sm:max-h-[540px]
          sm:rounded-2xl sm:border sm:border-[#E5E7EB]
          sm:shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25),0_4px_12px_-2px_rgba(15,23,42,0.08)]
          overflow-hidden
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#F1F2F4] bg-gradient-to-b from-[#FBFCFD] to-white">
          <div className="flex items-center gap-2.5">
            <h2 className="text-[15.5px] font-bold text-[#0F172A] tracking-[-0.01em]">
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#EF4444] text-white text-[10.5px] font-bold leading-none shadow-[0_1px_2px_rgba(239,68,68,0.4)]"
                aria-hidden="true"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              className="text-[12.5px] font-semibold text-[#2F9BF0] hover:text-[#1E88E5] transition-colors disabled:text-[#CBD5E1] disabled:cursor-not-allowed"
            >
              Mark all read
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close notifications"
              className="sm:hidden ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full text-[#64748B] hover:bg-[#F1F5F9] transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-2.5 pb-2.5 border-b border-[#F1F2F4]">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTab("all")}
              className={`flex items-center justify-center py-2 rounded-xl text-[13px] font-semibold transition-all ${tab === "all"
                ? "bg-[#EAF4FF] text-[#0F172A] border border-[#BFDBFE]"
                : "bg-[#F8FAFC] text-[#334155] border border-transparent hover:bg-[#F1F5F9]"
                }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setTab("unread")}
              className={`flex items-center justify-center gap-2 py-2 rounded-xl text-[13px] font-semibold transition-all ${tab === "unread"
                ? "bg-[#EAF4FF] text-[#0F172A] border border-[#BFDBFE]"
                : "bg-[#F8FAFC] text-[#334155] border border-transparent hover:bg-[#F1F5F9]"
                }`}
            >
              Unread
              {unreadCount > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-md bg-[#DCEBFF] text-[#2F9BF0] text-[11px] font-bold leading-none"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* List */}
        <div
          className="flex-1 overflow-y-auto divide-y divide-[#DBEAFE] [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {visible.length === 0 ? (
            <EmptyState tab={tab} />
          ) : (
            visible.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={() => markAsRead(n.id)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#F1F2F4] px-4 py-3 text-center bg-gradient-to-t from-[#FBFCFD] to-white">
          {/* <button
            type="button"
            className="text-[12.5px] font-semibold text-[#2F9BF0] hover:text-[#1E88E5] transition-colors"
            onClick={onClose}
          >
            View all notifications
          </button> */}
        </div>
      </div>
    </>
  );
};

export default NotificationDropdown;
